import { z } from 'zod';

import { PUBLIC_MDC_DATASTORE_URL } from 'tools/environments';

import {
	GameOverview,
	gameOverviewSchema,
} from './schemas/game-overview.schema';
import { LiveScores, liveScoresSchema } from './schemas/live-scores.schema';
import {
	MatchCommentary,
	matchCommentarySchema,
} from './schemas/match-commentary.schema';
import { MatchLineup, matchLineupSchema } from './schemas/match-lineup.schema';
import {
	PlayerMatchStats,
	playerMatchStatsSchema,
} from './schemas/player-match-stats.schema';
import { TeamStats, teamStatsSchema } from './schemas/team-stats.schema';

/**
 * Fetches one of the Matchday Centre JSON files published to the CDN
 * datastore for a given match, e.g.
 * `{NEXT_PUBLIC_MDC_DATASTORE_URL}fixtures/first-team/2021/chelsea-v-manchester-city/data/gameoverview.json`.
 */
const fetchMatchdayFile = async <TSchema extends z.ZodTypeAny>(
	matchPath: string,
	file: string,
	schema: TSchema
): Promise<z.infer<TSchema> | null> => {
	const url = `${PUBLIC_MDC_DATASTORE_URL}${matchPath}/data/${file}`;

	try {
		const response = await fetch(url, {
			method: 'GET',
			headers: {
				Accept: 'application/json',
			},
		});

		if (!response.ok) {
			console.warn(`Failed to fetch ${file}: ${response.status}`);
			return null;
		}

		const data = await response.json();
		const parsed = schema.safeParse(data);

		if (!parsed.success) {
			console.warn(
				`Matchday ${file} validation warnings`,
				parsed.error.issues.map(
					(issue) => `${issue.path.join('.')}: ${issue.message}`
				)
			);
			return data as z.infer<TSchema>;
		}

		return parsed.data;
	} catch (error) {
		console.error(`Error fetching matchday ${file}:`, error);
		return null;
	}
};

export const getMdcGameOverview = (
	matchPath: string
): Promise<GameOverview | null> =>
	fetchMatchdayFile(matchPath, 'gameoverview.json', gameOverviewSchema);

export const getMatchCommentary = (
	matchPath: string,
	page: number
): Promise<MatchCommentary | null> =>
	fetchMatchdayFile(
		matchPath,
		`matchcommentary-${page}.json`,
		matchCommentarySchema
	);

export const getTeamStats = (matchPath: string): Promise<TeamStats | null> =>
	fetchMatchdayFile(matchPath, 'getteamstats.json', teamStatsSchema);

export const getPlayerMatchStats = (
	matchPath: string
): Promise<PlayerMatchStats | null> =>
	fetchMatchdayFile(
		matchPath,
		'playermatchstats.json',
		playerMatchStatsSchema
	);

export const getMatchLineup = (
	matchPath: string
): Promise<MatchLineup | null> =>
	fetchMatchdayFile(matchPath, 'getbothteamslineup.json', matchLineupSchema);

export const getLiveScores = (matchPath: string): Promise<LiveScores | null> =>
	fetchMatchdayFile(matchPath, 'getlivescores.json', liveScoresSchema);
